import type { Product } from '@/types';
import cn from 'classnames';
import AnchorLink from '@/components/ui/links/anchor-link';
import routes from '@/config/routes';
import usePrice from '@/lib/hooks/use-price';
import placeholder from '@/assets/images/placeholders/product.svg';
import { isFree } from '@/lib/is-free';
import { useTranslation } from 'next-i18next';
import AddToCart from '../cart/add-to-cart';
import { PlusIcon } from '../icons/plus-icon';
import { getAIModelIcon } from '@/lib/get-icon';
import RatingsBadge from '../review/rating-badge';
import { useInSuccessfulOrders } from '@/lib/hooks/use-in-orders';
import ImageComponent from '@/components/ui/image';
import { useNSFWBlur } from '@/lib/hooks/use-nsfw-blur';
import NSFWToggle from '../nsfw/nsfw-toggle';
import FavoriteButton from '../favorite/favorite-button';
import { motion } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { useModalAction } from '@/components/modal-views/context';
import { PreviewIcon } from '@/components/icons/preview-icon';
import { DetailsIcon } from '@/components/icons/details-icon';

export default function Card({ product }: { product: Product }) {
  const { openModal } = useModalAction();
  const { shouldBlur } = useNSFWBlur(product);
  const {
    name,
    slug,
    image,
    shop,
    ai_model,
    type_id,
    store_review_amount,
    store_review_score,
  } = product ?? {};

  const { t } = useTranslation('common');
  const { price, basePrice, discount } = usePrice({
    amount: product.sale_price ? product.sale_price : product.price,
    baseAmount: product.price,
  });
  const isFreeItem = isFree(product?.sale_price ?? product?.price);

  // const { in_orders } = useInSuccessfulOrders(parseInt(product.id, 10));
  const in_orders = product?.has_purchased;

  const isVideoProduct =
    product.type_slug === 'video' && product.gallery?.length > 3;
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    if (isVideoProduct) {
      videoRefs.current.forEach((videoRef) => {
        if (videoRef) {
          videoRef.play().catch((error) => {
            console.log('Video autoplay failed:', error);
          });
        }
      });
    }
  }, [isVideoProduct]);

  const [isHovering, setIsHovering] = useState(false);
  const [isSquareImage, setIsSquareImage] = useState(false);
  const [isLandscapeImage, setIsLandscapeImage] = useState(false);

  useEffect(() => {
    if (image?.original) {
      const img = new Image();
      img.src = image.original;
      img.onload = () => {
        if (img.width === img.height) {
          setIsSquareImage(true);
        } else if (img.width > img.height) {
          setIsSquareImage(false);
          setIsLandscapeImage(true);
        } else {
          setIsSquareImage(false);
          setIsLandscapeImage(false);
        }
      };
    }
  }, [image?.original]);

  return (
    <motion.div
      className="group relative flex h-full max-w-[300px] flex-col overflow-hidden rounded-xl bg-transparent transition-all hover:shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <AnchorLink
        href={routes.productUrl(slug)}
        className={cn(
          'flex h-full flex-col',
          shouldBlur && 'pointer-events-none'
        )}
      >
        <div
          className={cn(
            'relative min-h-[170px] w-full rounded-xl',
            isSquareImage
              ? 'aspect-square'
              : isLandscapeImage
              ? 'aspect-video'
              : 'aspect-[3/4]'
          )}
        >
          {isVideoProduct ? (
            <>
              <div className="absolute inset-0 duration-300 transition-opacity group-hover:opacity-0">
                <ImageComponent
                  src={image?.thumbnail ?? placeholder}
                  alt={name}
                  fill
                  className="rounded-xl bg-gray-50 object-cover dark:bg-gray-900"
                />
              </div>
              <div className="relative grid h-full w-full grid-cols-2 opacity-0 duration-300 transition-opacity group-hover:opacity-100">
                {product.gallery.slice(0, 4).map((item, index) => (
                  <div
                    key={index}
                    className="relative flex items-center p-[0.7px]"
                  >
                    {isHovering && (
                      <video
                        ref={(el) => (videoRefs.current[index] = el)}
                        src={item.original}
                        className="relative z-[1] h-full w-full object-cover"
                        loop
                        muted
                        playsInline
                        autoPlay
                      />
                    )}
                    <div className="pointer-events-none absolute inset-0 z-[10] shadow-[inset_4px_0px_6px_rgba(255,255,255,0.5),inset_-4px_0px_6px_rgba(255,255,255,0.5)]" />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <ImageComponent
              src={image?.original ?? placeholder}
              alt={name}
              width={400}
              height={400}
              className={cn(
                'rounded-xl',
                isSquareImage ? 'object-cover' : 'object-contain',
                !isSquareImage && !isLandscapeImage && 'max-h-[280px]'
              )}
            />
          )}

          <div className="absolute inset-0 z-10 hidden items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 md:flex">
            <div className="flex gap-3 rounded-xl bg-dark/50 p-3 backdrop-blur-sm">
              <button
                className="flex flex-col items-center gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  openModal('PRODUCT_DETAILS', { slug });
                }}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-light transition-colors hover:bg-brand-dark">
                  <PreviewIcon className="h-4 w-4" />
                </div>
                <span className="text-xs font-medium text-light">
                  {t('text-preview')}
                </span>
              </button>
              <AnchorLink href={routes.productUrl(slug)}>
                <button className="flex flex-col items-center gap-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-light transition-colors hover:bg-brand-dark">
                    <DetailsIcon className="h-4 w-4" />
                  </div>
                  <span className="text-xs font-medium text-light">
                    {t('text-details')}
                  </span>
                </button>
              </AnchorLink>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col p-3 justify-end">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getAIModelIcon({
                iconName: ai_model?.slug,
                className: 'h-4 w-4 text-light-base',
                type_id: type_id?.toString(),
              })}
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {ai_model?.name}{' '}
                <span className="hidden md:inline">Prompt</span>
              </span>
            </div>
          </div>

          <div className="mt-2 flex flex-col items-start">
            <div className="flex w-full items-center justify-between">
              <h3 className="mb-1 truncate text-left text-sm font-semibold">
                {name}
              </h3>
              <div onClick={(e) => e.preventDefault()}>
                <FavoriteButton
                  productId={product.id}
                  size="xs"
                  iconOnly
                  className="!border-gray-200 !bg-transparent text-gray-500 hover:!bg-gray-100 hover:text-brand dark:!border-gray-800 dark:hover:!bg-gray-800"
                />
              </div>
            </div>

            <div className="mt-2 flex flex-col gap-1">
              {store_review_amount > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="flex items-center">
                    <RatingsBadge
                      rating={store_review_score}
                      className="!mx-0 !mr-1 h-2.5 !px-0"
                    />
                    ({store_review_amount})
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">{shop?.name}</div>
              </div>
            </div>

            <div className="mt-2 flex flex-col items-start">
              <span
                className={cn(
                  'text-bold text-sm font-semibold uppercase',
                  basePrice && 'text-[#0f743b] dark:text-brand'
                )}
              >
                US{isFreeItem ? t('text-free') : price}
              </span>
              {!isFreeItem && basePrice ? (
                <span className="text-xs font-medium text-dark-900 dark:text-dark-700">
                  <del>US{basePrice}</del>
                  <span className="px-1">{`(${discount} off)`}</span>
                </span>
              ) : (
                <span className="text-xs font-medium opacity-0">
                  -
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-auto p-3 pt-0">
          {!in_orders ? (
            <div className="flex gap-2">
              <AddToCart
                item={product}
                variant="outline"
                withPrice={false}
                className="!h-[40px] !min-h-[40px] flex-1 !content-center !items-center !justify-center !py-0 !text-center !align-middle text-xs md:!px-4 md:text-xs"
                leftIcon={
                  <PlusIcon className="mr-2 inline-block hidden h-4 w-4 md:block" />
                }
              />
            </div>
          ) : (
            <div className="flex gap-2">
              <AnchorLink
                href={`/products/${slug}`}
                className="transition-fill-colors pointer-events-auto relative flex min-h-[40px] flex-1 cursor-pointer items-center justify-center gap-2 rounded bg-brand py-2 px-4 font-semibold text-white opacity-100 duration-200 hover:bg-brand-dark focus:bg-brand-dark"
              >
                Open Product
              </AnchorLink>
            </div>
          )}
        </div>
      </AnchorLink>

      {shouldBlur && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 backdrop-blur-md">
          <div className="bg-dot-pattern absolute inset-0 opacity-5" />
          <div className="relative flex flex-col items-center gap-2">
            <div className="mb-1 h-1.5 w-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500" />
            <p className="text-sm font-medium text-foreground/90">
              Adult Content
            </p>
            <div className="rounded-lg border border-violet-500/20 bg-background/90 px-4 py-2 shadow-lg backdrop-blur-sm">
              <NSFWToggle size="lg" className="min-w-[120px]" />
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
